import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useProductName from 'brand-customization/useProductName';
import { Formik, Form, Field } from 'formik';

import useAssistantHistory from 'ai/hooks/useAssistantHistory';
import Spinner from 'components/common/Spinner';
import type { AssistantHistory, PendingAIRequest, AssistantRequest, AssistantResponse } from 'ai/types';
import Well from 'components/bootstrap/Well';
import Button from 'components/bootstrap/Button';
import Alert from 'components/bootstrap/Alert';
import IconButton from 'components/common/IconButton';
import ButtonGroup from 'components/bootstrap/ButtonGroup';
import ClipboardIconButton from 'components/common/ClipboardIconButton';
import Markdown from 'components/common/Markdown';
import AIQuotaInfo from 'ai/common/AIQuotaInfo';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useLocation from 'routing/useLocation';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import { getPathnameWithoutId } from 'util/URLUtils';
import UserNotification from 'util/UserNotification';
import OverlayTrigger from 'components/common/OverlayTrigger';
import { Input } from 'components/bootstrap';
import { Timestamp } from 'components/common';

import useAssistantSidebar from './useAssistantSidebar';

const SidebarPanel = styled.div`
  width: 25vw;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.global.contentBackground};
  padding: 0;
  overflow: auto;
`;

const SidebarContent = styled.div`
  flex: 1 1 auto;
  padding: 10px;
`;

const HistoryTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ChatBubble = styled(Well)`
  margin-left: auto;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

const TimestampText = styled.span`
  font-size: ${({ theme }) => theme.fonts.size.small};
  white-space: nowrap;
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`;

const Question = ({ request, timestamp = undefined }: { request: AssistantRequest; timestamp?: Date }) => (
  <QuestionContainer>
    <ChatBubble>
      AI summary of <strong>{request.payload.name}</strong>.
    </ChatBubble>
    {timestamp && (
      <TimestampText>
        <Timestamp dateTime={timestamp} format="dateHourAndMinute" />
      </TimestampText>
    )}
  </QuestionContainer>
);

type Rating = { rating: number; review?: string };
const ResponseRating = () => {
  const overlay = useRef<{ hide: () => void }>();
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const onRating = useCallback(
    (values: Rating) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.USER_SURVEY, {
        app_pathname: getPathnameWithoutId(pathname),
        app_value: values,
      });
      UserNotification.success('Thank you for your feedback!');
    },
    [pathname, sendTelemetry],
  );

  const onGoodRating = useCallback(() => onRating({ rating: 3 }), [onRating]);
  const onBadRating = useCallback(
    (rating: Rating) => {
      onRating(rating);
      overlay.current.hide();
    },
    [onRating],
  );
  const badRatingOverlay = useMemo(
    () => (
      <Formik onSubmit={onBadRating} initialValues={{ rating: 1, review: undefined }}>
        <Form>
          <Field name="review">
            {({ field: { name, value, onChange } }) => (
              <Input
                id={name}
                name={name}
                type="textarea"
                value={value}
                onChange={onChange}
                label={
                  <span>
                    What can be improved? <small className="text-muted">(Optional)</small>
                  </span>
                }
                rows={4}
              />
            )}
          </Field>
          <Button bsSize="xs" type="submit">
            Submit
          </Button>
        </Form>
      </Formik>
    ),
    [onBadRating],
  );

  return (
    <>
      <IconButton name="thumb_up" iconType="regular" size="xs" title="Great response!" onClick={onGoodRating} />
      <OverlayTrigger ref={overlay} overlay={badRatingOverlay} placement="bottom" trigger="click" rootClose>
        <IconButton name="thumb_down" iconType="regular" size="xs" title="Bad response!" />
      </OverlayTrigger>
    </>
  );
};

const ResponseActions = ({ response }: { response: AssistantResponse }) => (
  <ButtonGroup>
    <ResponseRating />
    <ClipboardIconButton text={response.text} buttonTitle="Copy response to clipboard" />
  </ButtonGroup>
);

const ResponseContainer = styled.div<{ $highlight?: boolean }>`
  margin-bottom: 20px;
  border-radius: 8px;
  padding: 10px;
  border: 1px solid transparent;
  animation: ${({ $highlight }) => ($highlight ? 'highlight-fade 5s' : 'none')};

  @keyframes highlight-fade {
    from {
      border-color: ${({ theme }) => theme.colors.variant.success};
      background-color: ${({ theme }) => theme.colors.background.secondaryNav};
    }

    to {
      border-color: transparent;
      background-color: inherit;
    }
  }
`;

const Separator = styled.div`
  margin: 20px 0;
  border-bottom: 1px solid;
`;

const Response = ({ response, highlight = false }: { response: AssistantResponse; highlight?: boolean }) => {
  const [showHighlight, setShowHighlight] = useState(highlight);

  useEffect(() => {
    if (highlight) {
      setShowHighlight(true);
      const timeout = setTimeout(() => setShowHighlight(false), 5000);

      return () => clearTimeout(timeout);
    }

    return () => {};
  }, [highlight]);

  return (
    <ResponseContainer $highlight={showHighlight}>
      <Markdown augment text={response.text} />
      <ResponseActions response={response} />
    </ResponseContainer>
  );
};

const AssistantEntry = ({ history, highlight = false }: { history: AssistantHistory; highlight?: boolean }) => (
  <HistoryTimeline>
    <Separator />
    <Question request={history.request} timestamp={history.timestamp} />
    <Response response={history.response} highlight={highlight} />
  </HistoryTimeline>
);

const PendingRequest = ({ pendingRequest }: { pendingRequest: PendingAIRequest }) => {
  if (pendingRequest === null) {
    return null;
  }

  const indicator =
    pendingRequest.isPending === true ? (
      <Spinner text="Generating response ..." />
    ) : (
      <Alert bsStyle="danger">
        Error generating response: {pendingRequest.error} <br />
        <Button bsStyle="link" onClick={pendingRequest.retry}>
          Click here to retry
        </Button>{' '}
      </Alert>
    );

  return (
    <HistoryTimeline>
      <Question request={pendingRequest.request} />
      {indicator}
    </HistoryTimeline>
  );
};

const StickyContainer = styled.div`
  position: sticky;
  background-color: ${({ theme }) => theme.colors.global.contentBackground};
  padding: 10px;
  z-index: 1000;
`;

const HeaderContainer = styled(StickyContainer)`
  top: 0;
`;
const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Header = () => {
  const { close } = useAssistantSidebar();
  const productName = useProductName();

  return (
    <HeaderContainer>
      <HeaderRow>
        <h3>{productName} Assistant</h3>
        <IconButton name="close" title="Close" onClick={close} />
      </HeaderRow>
      <AIQuotaInfo text="requests left today" />
    </HeaderContainer>
  );
};

const FooterContainer = styled(StickyContainer)`
  bottom: 0;
  font-size: ${({ theme }) => theme.fonts.size.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Footer = () => (
  <FooterContainer>
    AI-generated summaries may occasionally contain inaccurate or misleading information. Always verify critical data
    independently.
  </FooterContainer>
);

const AssistantSidebar = () => {
  const { data, isLoading } = useAssistantHistory();
  const { pendingRequest } = useAssistantSidebar();
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  useEffect(() => {
    if (!isLoading && messagesEndRef?.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
    }
  }, [isLoading]);

  return (
    <SidebarPanel>
      <Header />
      <SidebarContent>
        {isLoading ? (
          <Spinner />
        ) : (
          data.map((history, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <AssistantEntry key={idx} history={history} highlight={idx === data.length - 1 && !pendingRequest} />
          ))
        )}
        {!isLoading && data.length === 0 && !pendingRequest && (
          <Alert bsStyle="info">Your history is currently empty.</Alert>
        )}
        {pendingRequest && <PendingRequest pendingRequest={pendingRequest} />}
        <div ref={messagesEndRef} />
      </SidebarContent>
      <Footer />
    </SidebarPanel>
  );
};

const AISidebarWrapper = () => {
  const { isOpen } = useAssistantSidebar();

  return isOpen ? <AssistantSidebar /> : null;
};

export default AISidebarWrapper;
